import { Component, Input, OnInit } from '@angular/core';
import { DateFormat, KeyValueData } from '../../domains/common.domain';
import { FormatterData, InstallationFlowType } from '../../constants/app.constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { CommonService } from '../../services/common.service';
import { GridConnectionData } from '../technician-data/technician-data.domain';
import { FileService } from '../../services/file.service';
import { Document, DocumentResponse } from '../jurisdiction.domain';
import { InstallationApplicationData } from '../facilities-form/facilities-form.domain';
import { AllDropdownKeys } from '../jurisdiction.constants';
import { ErrorService } from '../../services/error/error.service';
import { EnvironmentService } from '../../services/environment.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DestroyableComponent } from 'src/app/common/destroyable.component';

@Component({
  selector: 'app-order-process-documents',
  styleUrls: ['./order-process-documents.component.scss'],
  templateUrl: './order-process-documents.component.html',
})
export class OrderProcessDocumentsComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean = false;

  newDocumentsGroup: FormGroup;
  newDocuments: FormArray;

  applicationData: GridConnectionData | InstallationApplicationData;
  applicationType: string;
  comment: string;
  connectionDate: string;
  dateFormat: DateFormat;
  desiredDate: string;
  currentInstallationFlow: string;
  documentsData: Document[];
  documentTypeKey: string;
  installationComment: string;
  installationFlowType: KeyValueData;
  isPowerFacilityAvailable: boolean;
  isGasFacilityAvailable: boolean;
  isWaterFacilityAvailable: boolean;
  isHeatingFacilityAvailable: boolean;
  powerTermsChecked: boolean;
  gasTermsChecked1: boolean;
  gasTermsChecked2: boolean;
  gasTermsChecked3: boolean;
  waterTermsChecked: boolean;
  heatingTermsChecked: boolean;
  showCheckbox3ForGas: boolean;
  showInstallationComment: boolean;
  showNewDocuments: boolean;

  private _documentIds: string[] = [];

  constructor(
    private commonService: CommonService,
    private errorService: ErrorService,
    private fileService: FileService,
    private localStorageService: LocalStorageService,
    private environmentService: EnvironmentService,
    private formBuilder: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.dateFormat = FormatterData.date;
    this.comment = '';
    this.connectionDate = '';
    this.desiredDate = '';
    this.installationComment = '';
    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow');
    this.applicationType = this.localStorageService.getItem('application_type');
    this.installationFlowType = InstallationFlowType;
    this.documentTypeKey = AllDropdownKeys.DocumentType;

    const facilityTypes = this.localStorageService.getItem('selected_facilities') || [];
    this.isPowerFacilityAvailable = facilityTypes.findIndex(facility => facility.key === 'power') > -1;
    this.isGasFacilityAvailable = facilityTypes.findIndex(facility => facility.key === 'gas') > -1;
    this.isWaterFacilityAvailable = facilityTypes.findIndex(facility => facility.key === 'water') > -1;
    this.isHeatingFacilityAvailable = facilityTypes.findIndex(facility => facility.key === 'heating') > -1;

    this.showInstallationComment =
      this.environmentService.showAdditionalComment &&
      [InstallationFlowType.NewInstallation, InstallationFlowType.UpdateInstallation].indexOf(this.currentInstallationFlow) > -1 &&
      this.isPowerFacilityAvailable;

    this.powerTermsChecked = true;
    this.gasTermsChecked1 = false;
    this.gasTermsChecked2 = false;
    this.gasTermsChecked3 = false;
    this.waterTermsChecked = true;
    this.heatingTermsChecked = true;
    this.showCheckbox3ForGas = this.environmentService.showGas3;
    this.showNewDocuments = this.environmentService.showNewDocuments;

    this.getData();
    this._prepareDocumentsForm();
  }

  addDocument(): void {
    this.newDocuments.push(
      this.formBuilder.group({
        documentName: new FormControl('', [Validators.required]),
        fileName: new FormControl(''),
      })
    );
  }

  onDocumentUpload(event: any, index: number, uploader: any): void {
    const applicationId = this.localStorageService.getItem('application_id');
    const documentName = this.newDocuments.at(index).value.documentName;

    if (this._documentIds[index] !== undefined) {
      this.preventLeak(this.fileService.modifyDocumentForWorkflow(event, this._documentIds[index])).subscribe(
        (response: any) => {
          uploader.clear();
          (this.newDocuments.at(index) as FormGroup).controls.fileName.setValue(
            event.currentFiles && event.currentFiles[0] && event.currentFiles[0].name
          );
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          uploader.clear();
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.preventLeak(this.fileService.uploadDocumentsForWorkflows(event, applicationId, 'other', documentName)).subscribe(
        (response: DocumentResponse) => {
          (this.newDocuments.at(index) as FormGroup).controls.fileName.setValue(
            event.currentFiles && event.currentFiles[0] && event.currentFiles[0].name
          );
          this._documentIds[index] = response.document[0].document_id;
          uploader.clear();
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          uploader.clear();
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  removeDocument(id: number): void {
    if (this._documentIds[id] !== undefined) {
      this.preventLeak(this.fileService.removeWorkflowDocument(this._documentIds[id])).subscribe(
        response => {
          this.newDocuments.removeAt(id);
          this._documentIds.splice(id, 1);
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.newDocuments.removeAt(id);
    }
  }

  downloadDocument(documentId: string, fileName?: string): void {
    this.preventLeak(this.fileService.readWorkflowDocument(documentId)).subscribe(
      (response: Blob) => {
        this.errorService.throwErrorIfFailure(response, true);
        this.fileService.downloadDocument(response, fileName);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private getData(): void {
    const applicationTypes = this.commonService.getDropdownData(AllDropdownKeys.ApplicationType);
    switch (this.applicationType) {
      case applicationTypes[0].value:
        this.applicationData = this.commonService.installationApplicationData;
        break;
      case applicationTypes[1].value:
        this.applicationData = this.commonService.gridConnectionApplicationData;
        break;
    }
    this.documentsData = (this.applicationData && this.applicationData.document) || [];
    this.comment = this.applicationData.comment;
    this.installationComment = (this.applicationData as InstallationApplicationData).installation_comment || '';
    this.desiredDate = this.applicationData.desired_date ? this.commonService.parseDateForUI(this.applicationData.desired_date) : '';
    this.connectionDate = this.applicationData.building_con_from
      ? this.commonService.parseDateForUI(this.applicationData.building_con_from)
      : '';
    if (
      this.isGasFacilityAvailable &&
      (this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
        this.currentInstallationFlow === this.installationFlowType.UpdateInstallation)
    ) {
      this.gasTermsChecked1 = (this.applicationData as InstallationApplicationData).gasinstallation.confirm_ndav === '1';
      this.gasTermsChecked2 = (this.applicationData as InstallationApplicationData).gasinstallation.confirm_g600 === '1';
      this.gasTermsChecked3 = (this.applicationData as InstallationApplicationData).gasinstallation.confirm_chimneysweeper_report === '1';
    }
  }

  private _prepareDocumentsForm(): void {
    this.newDocumentsGroup = this.formBuilder.group({
      newDocuments: this.formBuilder.array([]),
    });

    this.newDocuments = this.newDocumentsGroup.get('newDocuments') as FormArray;
  }
}
